import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import ClassList from "./ClassList";
import { displayStatusOf } from "./workflowMeta";

const DOT = {
  teacher_draft: "bg-slate-300",
  changes_requested: "bg-rose-500",
  hod_review: "bg-amber-500",
  admin_review: "bg-blue-500",
  approved: "bg-emerald-500",
};

/**
 * Class switcher as a dropdown instead of a permanent side rail, so the plan
 * gets the full page width. The list (with search) opens on demand.
 */
export default function ClassPicker({ rows, plans, selectedUid, onSelect, query, onQuery, extra, count }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = rows.find((r) => r.uid === selectedUid);
  const status = displayStatusOf(plans[selectedUid]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex w-full min-w-[260px] items-center gap-3 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-left shadow-sm hover:border-[#1565c0] sm:w-auto"
      >
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${DOT[status] || DOT.teacher_draft}`} />
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-medium uppercase tracking-wide text-slate-500">Class{count > 1 ? ` · ${count} to choose from` : ""}</span>
          <span className="block truncate text-[15px] font-semibold text-slate-900">
            {selected ? `${selected.grade}-${selected.section} · ${selected.subject}` : "Choose a class"}
          </span>
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 z-40 mt-2 w-[min(420px,92vw)] shadow-xl">
          <ClassList
            rows={rows}
            plans={plans}
            selectedUid={selectedUid}
            onSelect={(uid) => {
              onSelect(uid);
              setOpen(false);
            }}
            query={query}
            onQuery={onQuery}
            extra={extra}
          />
        </div>
      )}
    </div>
  );
}
